/**
 * Brief 3.10 — AM document review actions (Reject, Withdraw, AI-check,
 * Request replacement). Inline dropdown menu on every uploaded doc row.
 *
 * Extracted from DocumentsTable.jsx in the Feb-21 cleanup pass.
 */
import React, { useEffect, useState } from "react";
import {
  Loader2, MoreHorizontal, Sparkles, XCircle, Trash2, MessageSquare,
  ShieldCheck,
} from "lucide-react";
import api from "../../../lib/api";


export default function DocReviewActions({ merchantId, doc, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // "reject" | "ai" | "replace"
  const [reason, setReason] = useState("");
  const [aiCheck, setAiCheck] = useState(null);
  const [error, setError] = useState("");

  // Load any existing AI verdict on mount so the badge reflects the
  // latest async background check.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/merchants/${merchantId}/documents/${doc.doc_id}/ai-check`,
        );
        if (alive && data?.check) setAiCheck(data.check);
      } catch { /* no existing check */ }
    })();
    return () => { alive = false; };
  }, [merchantId, doc.doc_id]);

  const handleReject = async () => {
    if (reason.trim().length < 4) {
      setError("Reason needs at least 4 characters.");
      return;
    }
    setBusy(true); setError("");
    try {
      await api.post(
        `/admin/merchants/${merchantId}/documents/${doc.doc_id}/reject`,
        { reason: reason.trim() },
      );
      setModal(null); setOpen(false); setReason("");
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Reject failed.");
    } finally { setBusy(false); }
  };

  const handleWithdraw = async () => {
    if (!confirm("Soft-delete this document? It will disappear from the merchant's slot view. This is audit-logged."))
      return;
    setBusy(true);
    try {
      await api.delete(
        `/admin/merchants/${merchantId}/documents/${doc.doc_id}`,
      );
      setOpen(false);
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const handleRunAiCheck = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        `/admin/merchants/${merchantId}/documents/${doc.doc_id}/ai-check`,
      );
      setAiCheck(data);
      setModal("ai");
    } catch (e) {
      setError(e?.response?.data?.detail || "AI check failed.");
      setModal("ai");
    } finally { setBusy(false); }
  };

  const useAiDraftAsReason = () => {
    if (aiCheck?.draft_rejection_note) {
      setReason(aiCheck.draft_rejection_note);
      setModal("reject");
    }
  };

  /**
   * AI verdict override — when AI flagged a mismatch but the AM has
   * eyeballed the file and it's actually the right document. Flips
   * the verdict to "match" + clears the "Blocks approval" critical
   * finding on the row by updating extraction_status server-side.
   */
  const handleMarkVerified = async () => {
    const hasWarning = (
      aiCheck?.verdict === "mismatch" ||
      doc.extraction_status === "doc_type_mismatch" ||
      doc.finding_severity === "critical"
    );
    const confirmMsg = hasWarning
      ? ("Override the AI verdict and mark this document as verified? " +
         "The mismatch warning and 'Blocks approval' flag will be cleared. Audit-logged.")
      : ("Mark this document as verified? It will be flagged as manually " +
         "reviewed and accepted by you. Audit-logged.");
    if (!window.confirm(confirmMsg)) return;
    setBusy(true); setError(""); setOpen(false);
    try {
      const { data } = await api.post(
        `/admin/merchants/${merchantId}/documents/${doc.doc_id}/mark-verified`,
      );
      if (data?.check) setAiCheck(data.check);
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't mark as verified.");
    } finally { setBusy(false); }
  };

  // Feb-25 2026 — AMs asked for the ability to mark ANY uploaded
  // document as verified, not just AI-flagged ones. Previously the
  // button only appeared on mismatches/critical findings, which
  // meant once those cleared the button vanished and AMs had no
  // way to sign off on the rest of the slot. Now:
  //   • Show "Mark as verified" on every currently-uploaded document
  //     that hasn't been manually verified yet.
  //   • Hide it once `extraction_status === "manually_verified"` or
  //     the latest AI check is an admin override.
  const isAlreadyVerified = (
    doc.extraction_status === "manually_verified" ||
    aiCheck?.override === true
  );
  const showOverride = doc.state === "uploaded" && !isAlreadyVerified;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`doc-actions-${doc.doc_id}`}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.02] px-2 py-1 text-[11px] text-zinc-300 hover:border-white/30 hover:bg-white/[0.06] disabled:opacity-40"
        title="Review actions"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MoreHorizontal className="h-3 w-3" />}
      </button>
      {aiCheck?.verdict === "mismatch" && (
        <span
          data-testid={`doc-ai-mismatch-${doc.doc_id}`}
          title={aiCheck.reason}
          className="absolute -top-1 -right-1 inline-flex items-center rounded-full bg-rose-500 text-white px-1 text-[8px] font-bold"
        >!</span>
      )}
      {isAlreadyVerified && (
        <span
          data-testid={`doc-verified-badge-${doc.doc_id}`}
          title="Marked as verified by an admin"
          className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white h-3.5 w-3.5 text-[8px] font-bold"
        >✓</span>
      )}
      {open && (
        <div className="absolute right-0 mt-1 z-20 w-52 rounded-lg border border-white/10 bg-[#0f0f0f] shadow-xl py-1" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={handleRunAiCheck}
            data-testid={`doc-action-ai-${doc.doc_id}`}
            className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-zinc-200 hover:bg-white/[0.04]"
          >
            <Sparkles className="h-3 w-3 text-[#00E5FF]" /> Run AI type check
          </button>
          {showOverride && (
            <button
              type="button"
              onClick={handleMarkVerified}
              data-testid={`doc-action-mark-verified-${doc.doc_id}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-emerald-300 hover:bg-emerald-400/[0.06]"
              title="Override AI: confirm this is the right document and clear the warnings"
            >
              <ShieldCheck className="h-3 w-3" /> Mark as verified
            </button>
          )}
          <button
            type="button"
            onClick={() => { setModal("reject"); setOpen(false); }}
            data-testid={`doc-action-reject-${doc.doc_id}`}
            className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-rose-300 hover:bg-rose-400/[0.06]"
          >
            <XCircle className="h-3 w-3" /> Reject with reason
          </button>
          <button
            type="button"
            onClick={() => { setModal("replace"); setOpen(false); }}
            data-testid={`doc-action-replace-${doc.doc_id}`}
            className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-zinc-200 hover:bg-white/[0.04]"
          >
            <MessageSquare className="h-3 w-3" /> Request replacement…
          </button>
          <div className="my-1 border-t border-white/[0.06]" />
          <button
            type="button"
            onClick={handleWithdraw}
            data-testid={`doc-action-withdraw-${doc.doc_id}`}
            className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-zinc-400 hover:bg-white/[0.04]"
          >
            <Trash2 className="h-3 w-3" /> Soft-delete (withdraw)
          </button>
        </div>
      )}

      {modal === "reject" && (
        <ActionModal title="Reject document" onClose={() => setModal(null)}>
          <p className="text-[12.5px] text-zinc-400 leading-relaxed">
            The merchant will see this reason and a &ldquo;please re-upload&rdquo; pointer
            on their dashboard. The slot will be cleared.
          </p>
          {aiCheck?.draft_rejection_note && reason !== aiCheck.draft_rejection_note && (
            <button
              type="button"
              onClick={() => setReason(aiCheck.draft_rejection_note)}
              data-testid={`doc-reject-use-ai-${doc.doc_id}`}
              className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] text-cyan-300 hover:text-cyan-200"
            >
              <Sparkles className="h-3 w-3" /> Use AI-suggested reason
            </button>
          )}
          <textarea
            data-testid={`doc-reject-reason-${doc.doc_id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What was wrong with this upload? Be specific."
            maxLength={500}
            rows={4}
            className="mt-3 w-full rounded-md border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-rose-400/40 resize-none"
          />
          {error && <div className="mt-2 text-[11.5px] text-rose-300">{error}</div>}
          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={() => setModal(null)} className="px-3 py-1.5 text-[12px] text-zinc-400 hover:text-white">Cancel</button>
            <button
              type="button" onClick={handleReject} disabled={busy}
              data-testid={`doc-reject-submit-${doc.doc_id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/80 hover:bg-rose-500 text-white px-4 py-1.5 text-[12px] font-medium disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
              Reject and notify merchant
            </button>
          </div>
        </ActionModal>
      )}

      {modal === "replace" && (
        <ReplacementRequestModal
          merchantId={merchantId}
          docKey={doc.doc_key}
          onClose={() => setModal(null)}
          onSubmitted={() => { setModal(null); onChanged && onChanged(); }}
        />
      )}

      {modal === "ai" && aiCheck && (
        <ActionModal title="AI type check" onClose={() => setModal(null)}>
          <div className="flex items-center gap-2">
            <VerdictPill verdict={aiCheck.verdict} confidence={aiCheck.confidence} />
            <span className="text-[11px] text-zinc-500">{aiCheck.model || "n/a"}</span>
          </div>
          <div className="mt-3 text-[12.5px] text-zinc-300 leading-relaxed" data-testid={`doc-ai-reason-${doc.doc_id}`}>
            {aiCheck.reason || "No detail returned."}
          </div>
          {aiCheck.draft_rejection_note && (
            <div className="mt-4 rounded-md border border-amber-400/20 bg-amber-400/[0.04] p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300">Draft rejection note</div>
              <div className="mt-1 text-[12.5px] text-amber-100" data-testid={`doc-ai-draft-${doc.doc_id}`}>
                {aiCheck.draft_rejection_note}
              </div>
            </div>
          )}
          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={() => setModal(null)} className="px-3 py-1.5 text-[12px] text-zinc-400 hover:text-white">Close</button>
            {aiCheck.draft_rejection_note && (
              <button
                type="button" onClick={useAiDraftAsReason}
                data-testid={`doc-ai-use-draft-${doc.doc_id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/80 hover:bg-rose-500 text-white px-4 py-1.5 text-[12px] font-medium"
              >
                <XCircle className="h-3 w-3" /> Reject using this note
              </button>
            )}
          </div>
        </ActionModal>
      )}
    </div>
  );
}

function ActionModal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#111] p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-white">{title}</h3>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

function VerdictPill({ verdict, confidence }) {
  const tone =
    verdict === "match" ? "bg-emerald-400/10 text-emerald-200 border-emerald-400/30"
    : verdict === "mismatch" ? "bg-rose-400/10 text-rose-200 border-rose-400/30"
    : "bg-zinc-400/10 text-zinc-300 border-zinc-400/20";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${tone}`}>
      {verdict} · {confidence || "?"}
    </span>
  );
}

function ReplacementRequestModal({ merchantId, docKey, onClose, onSubmitted }) {
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (instruction.trim().length < 4) {
      setError("Instruction needs at least 4 characters.");
      return;
    }
    setBusy(true); setError("");
    try {
      await api.post(
        `/admin/merchants/${merchantId}/documents/replacement-request`,
        { doc_key: docKey, instruction: instruction.trim() },
      );
      onSubmitted();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't create replacement request.");
    } finally { setBusy(false); }
  };

  return (
    <ActionModal title="Request a replacement upload" onClose={onClose}>
      <p className="text-[12.5px] text-zinc-400 leading-relaxed">
        This creates a &ldquo;please upload&rdquo; pointer for the merchant tied to the slot <code className="text-zinc-200">{docKey}</code>. Idempotent — re-submitting overwrites the previous instruction.
      </p>
      <textarea
        data-testid="doc-replace-instruction"
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        placeholder="Explain what you need them to upload. Be specific."
        maxLength={500}
        rows={4}
        className="mt-3 w-full rounded-md border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 resize-none"
      />
      {error && <div className="mt-2 text-[11.5px] text-rose-300">{error}</div>}
      <div className="mt-4 flex items-center justify-end gap-2">
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12px] text-zinc-400 hover:text-white">Cancel</button>
        <button
          type="button" onClick={submit} disabled={busy}
          data-testid="doc-replace-submit"
          className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/80 hover:bg-cyan-500 text-black px-4 py-1.5 text-[12px] font-medium disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageSquare className="h-3 w-3" />}
          Send request
        </button>
      </div>
    </ActionModal>
  );
}
